import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import { Globe2, MapPin, PackageSearch, Sparkles, ShieldAlert } from "lucide-react";

function BidDialog({ inquiry, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const existing = inquiry.my_bid;

  const [f, setF] = useState({
    price_per_unit: "",
    currency: "EUR",
    moq_units: "",
    lead_time_days: "",
    notes: "",
  });

  useEffect(() => {
    if (open) {
      setF({
        price_per_unit: existing?.price_per_unit ?? "",
        currency: existing?.currency ?? "EUR",
        moq_units: existing?.moq_units ?? "",
        lead_time_days: existing?.lead_time_days ?? "",
        notes: existing?.notes ?? "",
      });
    }
  }, [open, existing]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        price_per_unit: Number(f.price_per_unit),
        currency: f.currency,
        moq_units: f.moq_units,
        lead_time_days: Number(f.lead_time_days),
        notes: f.notes || undefined,
      };
      await api.post(`/rfq/${inquiry.id}/bids`, payload);
      toast.success(existing ? "Bid updated" : "Bid submitted");
      setOpen(false);
      onSubmitted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit bid");
    } finally {
      setBusy(false);
    }
  };

  const isDecided = existing?.status === "accepted" || existing?.status === "rejected";
  const cta = existing ? (isDecided ? "View bid" : "Edit bid") : "Submit bid";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid={`rfq-bid-btn-${inquiry.id}`}
          size="sm"
          variant={existing ? "outline" : "default"}
          className="h-9"
        >
          {cta}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={`rfq-bid-dialog-${inquiry.id}`}>
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">
            Bid on RFQ · {inquiry.category}
          </DialogTitle>
          <DialogDescription>
            Buyer identity is hidden until they accept your bid. Target volume: {inquiry.target_volume}{" "}
            {inquiry.unit || ""} · Destination {inquiry.destination_country}.
          </DialogDescription>
        </DialogHeader>
        {isDecided && (
          <div
            className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground"
            data-testid={`rfq-decided-note-${inquiry.id}`}
          >
            This bid was <span className="font-bold text-secondary">{existing.status}</span>. It can no longer be edited.
          </div>
        )}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Price per unit</Label>
              <Input
                data-testid={`bid-price-${inquiry.id}`}
                type="number"
                step="0.01"
                min="0"
                value={f.price_per_unit}
                onChange={(e) => setF({ ...f, price_per_unit: e.target.value })}
                disabled={isDecided}
              />
            </div>
            <div>
              <Label>Currency</Label>
              <select
                data-testid={`bid-currency-${inquiry.id}`}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={f.currency}
                onChange={(e) => setF({ ...f, currency: e.target.value })}
                disabled={isDecided}
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="VND">VND</option>
              </select>
            </div>
          </div>
          <div>
            <Label>MOQ (min. order quantity)</Label>
            <Input
              data-testid={`bid-moq-${inquiry.id}`}
              value={f.moq_units}
              onChange={(e) => setF({ ...f, moq_units: e.target.value })}
              placeholder="e.g. 1 pallet · 500 bags · 5 TEU"
              disabled={isDecided}
            />
          </div>
          <div>
            <Label>Lead time (days)</Label>
            <Input
              data-testid={`bid-lead-${inquiry.id}`}
              type="number"
              min="0"
              value={f.lead_time_days}
              onChange={(e) => setF({ ...f, lead_time_days: e.target.value })}
              disabled={isDecided}
            />
          </div>
          <div>
            <Label>Notes (specs, certifications, packaging)</Label>
            <Textarea
              data-testid={`bid-notes-${inquiry.id}`}
              rows={4}
              value={f.notes}
              onChange={(e) => setF({ ...f, notes: e.target.value })}
              disabled={isDecided}
              placeholder="Include EU-relevant certifications (HACCP, ISO, organic), Incoterms, packaging, and any assumptions."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          {!isDecided && (
            <Button
              data-testid={`bid-submit-${inquiry.id}`}
              onClick={submit}
              disabled={
                busy ||
                !f.price_per_unit ||
                !f.moq_units ||
                f.lead_time_days === "" ||
                Number(f.lead_time_days) < 0
              }
            >
              {busy ? "Submitting…" : existing ? "Update bid" : "Submit bid"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RFQCard({ inquiry, onChanged }) {
  const bidStatus = inquiry.my_bid?.status;
  return (
    <div
      data-testid={`rfq-card-${inquiry.id}`}
      className="rounded-lg border border-border bg-white p-6 flex flex-col gap-4 hover:border-primary/50 transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="overline text-primary text-[10px]">{inquiry.category}</div>
          <div className="mt-1 font-heading text-lg font-black text-secondary leading-snug">
            {inquiry.target_volume} {inquiry.unit || ""}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> Destination · {inquiry.destination_country}
            </span>
            {inquiry.buyer_country && (
              <span className="inline-flex items-center gap-1">
                <Globe2 className="w-3.5 h-3.5" /> Buyer HQ · {inquiry.buyer_country}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <StatusBadge status={inquiry.status} />
          <span className="text-[10px] text-muted-foreground">
            {inquiry.bid_count} bid{inquiry.bid_count === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      {inquiry.budget && (
        <div className="rounded-md bg-primary/5 border border-primary/20 px-3 py-2 text-xs text-secondary">
          <span className="font-heading font-black text-primary uppercase tracking-widest text-[10px] mr-2">
            Budget
          </span>
          {inquiry.budget}
        </div>
      )}

      {inquiry.notes && (
        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-4 whitespace-pre-wrap">
          {inquiry.notes}
        </p>
      )}

      <div className="mt-auto flex items-center justify-between pt-2 border-t border-border">
        <div className="text-xs text-muted-foreground">
          Posted {new Date(inquiry.created_at).toLocaleDateString()}
        </div>
        <div className="flex items-center gap-2">
          {bidStatus && (
            <span
              data-testid={`rfq-mybid-${inquiry.id}`}
              className={`text-[10px] font-heading font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                bidStatus === "accepted"
                  ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                  : bidStatus === "rejected"
                  ? "text-red-700 bg-red-50 border-red-200"
                  : "text-primary bg-primary/10 border-primary/25"
              }`}
            >
              Your bid · {bidStatus}
            </span>
          )}
          <BidDialog inquiry={inquiry} onSubmitted={onChanged} />
        </div>
      </div>
    </div>
  );
}

export default function SupplierRFQBoard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .get("/rfq/board")
      .then((r) => setItems(r.data))
      .catch((e) => {
        const msg = formatApiErrorDetail(e.response?.data?.detail) || "Could not load the RFQ board";
        setError(msg);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (i) =>
        (i.category || "").toLowerCase().includes(q) ||
        (i.destination_country || "").toLowerCase().includes(q) ||
        (i.notes || "").toLowerCase().includes(q),
    );
  }, [items, filter]);

  const gatedByApproval = user?.status && user.status !== "approved";

  return (
    <DashboardLayout
      title="RFQ Marketplace"
      actions={
        <div className="hidden md:flex items-center gap-2 text-xs font-heading font-black uppercase tracking-widest text-primary">
          <Sparkles className="w-4 h-4" /> Live sourcing feed
        </div>
      }
    >
      <div className="mb-6 rounded-lg border border-primary/25 bg-primary/5 p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="max-w-2xl">
          <div className="font-heading text-xl font-black text-secondary">
            Open bulk sourcing requests from EU buyers
          </div>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            Buyer identity stays anonymous until you win. Submit your best price, MOQ and lead time —
            the buyer picks the winning bid and Safeway Finland facilitates the handshake.
          </p>
        </div>
        <div className="w-full md:w-72">
          <Input
            data-testid="rfq-filter"
            placeholder="Filter by category, country, notes…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      {gatedByApproval && (
        <Card className="mb-6 border-amber-300 bg-amber-50">
          <CardContent className="p-5 flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-secondary">
              Your supplier account is <span className="font-bold">{user?.status || "pending"}</span>. Once Safeway Finland
              approves you, you&apos;ll be able to submit bids on the marketplace.
            </div>
          </CardContent>
        </Card>
      )}

      {error && (
        <Card className="mb-6 border-red-300 bg-red-50">
          <CardContent className="p-5 flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="text-sm text-secondary" data-testid="rfq-error">{error}</div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="py-16 text-center text-sm text-muted-foreground" data-testid="rfq-loading">Loading open RFQs…</div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="rfq-empty"
          className="py-16 text-center rounded-lg border border-dashed border-border bg-white"
        >
          <PackageSearch className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <div className="font-heading text-lg font-bold text-secondary">No open RFQs match your filter.</div>
          <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
            New bulk requests from EU buyers land here in real time. Check back soon or clear the filter.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="rfq-grid">
          {filtered.map((i) => (
            <RFQCard key={i.id} inquiry={i} onChanged={load} />
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
